import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faEdit,
  faSave,
  faUser,
  faCalendar,
  faFlag,
  faTasks,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

import { taskService } from '../../services/taskService';

const TaskDetailsModal = ({ 
  task, 
  onClose, 
  onUpdate, 
  onStatusUpdate 
}) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: task.title,
    description: task.description || '',
    priority: task.priority,
    status: task.status,
    assigned_to: task.assigned_to || '',
    due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : ''
  });
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);

  useEffect(() => {
    loadUsers();
    loadComments();
  }, [task.id]);

  const loadUsers = async () => {
    try {
      const usersData = await taskService.getUsersForAssignment();
      // Handle the response format from /api/users/ endpoint
      if (usersData.success && usersData.users) {
        setUsers(usersData.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const loadComments = async () => {
    try {
      setLoadingComments(true);
      const commentsData = await taskService.getTaskComments(task.id);
      setComments(commentsData.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const updateData = {
        ...formData,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : null,
        due_date: formData.due_date || null
      };
      
      await onUpdate(task.id, updateData);
      setEditing(false);
      toast.success('Task updated successfully!');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await taskService.addTaskComment(task.id, newComment.trim());
      setNewComment('');
      loadComments();
      toast.success('Comment added successfully!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'text-gray-700 bg-gray-100',
      in_progress: 'text-blue-700 bg-blue-100',
      completed: 'text-green-700 bg-green-100',
      cancelled: 'text-red-700 bg-red-100'
    };
    return colors[status] || colors.todo;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-700 bg-gray-100',
      medium: 'text-yellow-700 bg-yellow-100',
      high: 'text-orange-700 bg-orange-100',
      urgent: 'text-red-700 bg-red-100'
    };
    return colors[priority] || colors.medium;
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={{ 
        hidden: { opacity: 0 }, 
        visible: { opacity: 1 }, 
        exit: { opacity: 0 } 
      }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content max-w-4xl"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faTasks} className="text-indigo-600 mr-2" />
            <h2 className="modal-title">Task Details</h2>
          </div>
          <div className="flex items-center space-x-2">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Edit Task"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={loading}
                className="p-2 rounded-lg text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                title="Save Changes"
              >
                <FontAwesomeIcon icon={faSave} />
              </button>
            )}
            <button onClick={onClose} className="modal-close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Task Details */}
            <div>
              {/* Task Title */}
              <div className="form-group">
                <label className="form-label">Title</label>
                {editing ? (
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                ) : (
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {task.title}
                  </h3>
                )}
              </div>

              {/* Task Description */}
              <div className="form-group">
                <label className="form-label">Description</label>
                {editing ? (
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows={4}
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    {task.description || 'No description provided.'}
                  </p>
                )}
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  {editing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
                      {task.status.replace('_', ' ').toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  {editing ? (
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(task.priority)}`}>
                      <FontAwesomeIcon icon={faFlag} className="mr-1" />
                      {task.priority.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>

              {/* Assignment and Due Date */}
              <div className="grid grid-cols-1 gap-4">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  {editing ? (
                    <select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select assignee...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                      {task.assignee_name || 'Not assigned'}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  {editing ? (
                    <input
                      type="datetime-local"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  ) : (
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCalendar} className="text-gray-400 mr-2" />
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Comments */}
            <div>
              <div className="form-group">
                <label className="form-label flex items-center">
                  <FontAwesomeIcon icon={faComment} className="mr-2" />
                  Comments ({comments.length})
                </label>
                
                {/* Add Comment */}
                <div className="mb-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="form-textarea"
                    rows={3}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="btn-primary mt-2"
                  >
                    Add Comment
                  </button>
                </div>

                {/* Comments List */}
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {loadingComments ? (
                    <div className="text-center py-4">
                      <div className="loading-spinner mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading comments...</p>
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">
                      No comments yet. Be the first to comment!
                    </p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">
                            {comment.user_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {comment.comment}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Task Meta Information */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <p>Created by {task.creator_name} on {new Date(task.created_at).toLocaleDateString()}</p>
              <p>Last updated on {new Date(task.updated_at).toLocaleDateString()}</p>
              {task.completed_at && (
                <p>Completed on {new Date(task.completed_at).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskDetailsModal;